// File: frontend/src/components/marketing/partners/PartnersPrimitives.js
//
// Layout and call-to-action building blocks shared by the /ambassador sections.
//
// Surfaces read the page-level CSS variables PartnersLanding defines
// (--amb-surface, --amb-surface-strong, --amb-line, --amb-accent-soft,
// --amb-accent-line). They are derived from theme.js tokens, so both colour
// modes come from the app's single theme; there is no second theme system.

import React, { useState } from "react";
import { Box, Container, Typography, Button, Stack, alpha } from "@mui/material";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { Link as RouterLink } from "react-router-dom";

import { ambassadorUrl } from "../../../config/origins";
import { openWorkspace } from "../../../services/handoff";

// ═══════════════════════════════════════════════════════════════════════════
// LAYOUT
// ═══════════════════════════════════════════════════════════════════════════

export const Section = React.forwardRef(function Section(
  { id, labelledBy, children, sx, maxWidth = "lg" },
  ref
) {
  return (
    <Box
      ref={ref}
      component="section"
      id={id}
      aria-labelledby={labelledBy}
      sx={{ py: { xs: 7, md: 11 }, scrollMarginTop: 72, position: "relative", ...sx }}
    >
      <Container maxWidth={maxWidth} sx={{ minWidth: 0 }}>{children}</Container>
    </Box>
  );
});

export const H2_SX = {
  color: "text.primary",
  fontWeight: 800,
  fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.4rem" },
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
};

export function SectionHeading({ id, eyebrow, title, subtitle, align = "center", sx }) {
  const center = align === "center";
  return (
    <Box
      sx={{
        textAlign: center ? "center" : "left",
        maxWidth: center ? 760 : 620,
        mx: center ? "auto" : 0,
        mb: { xs: 4, md: 6 },
        ...sx,
      }}
    >
      {eyebrow && (
        <Typography data-reveal="fade" variant="overline" component="p" sx={{ color: "primary.main", mb: 1.25 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography data-reveal id={id} variant="h2" component="h2" sx={H2_SX}>
        {title}
      </Typography>
      {subtitle && (
        <Typography data-reveal variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/** A readable surface on top of the ambient backdrop. */
export function Surface({ children, sx, strong = false, ...rest }) {
  return (
    <Box
      {...rest}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "var(--amb-line)",
        bgcolor: strong ? "var(--amb-surface-strong)" : "var(--amb-surface)",
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Square icon tile used across sections. */
export function IconTile({ icon: Icon, size = 44, sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, borderRadius: 2.5, flexShrink: 0,
        display: "grid", placeItems: "center",
        bgcolor: "var(--amb-accent-soft)",
        color: "primary.main",
        ...sx,
      }}
    >
      <Icon sx={{ fontSize: Math.round(size * 0.5) }} />
    </Box>
  );
}

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// ═══════════════════════════════════════════════════════════════════════════
// CALLS TO ACTION
// ═══════════════════════════════════════════════════════════════════════════
//
// An action is one of:
//   { kind: "external",  label, href }   full navigation (the Ambassador host)
//   { kind: "workspace", label, path }   openWorkspace("ambassador", path)
//   { kind: "route",     label, to }     same-host React Router link
//
// Every action renders as a real anchor with a real href, so middle-click,
// "copy link" and no-JS all still work. A workspace action intercepts a plain
// left click to run the signed-in handoff instead of a bare navigation.

const isModifiedClick = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;

export function CtaButton({ action, variant = "contained", size = "large", fullWidth, onNavigate, sx }) {
  const [busy, setBusy] = useState(false);
  if (!action) return null;

  const base = {
    variant,
    size,
    fullWidth,
    sx: {
      borderRadius: 2,
      minHeight: size === "large" ? 48 : 40,
      px: size === "large" ? 3.5 : 2,
      fontWeight: variant === "contained" ? 700 : 600,
      whiteSpace: "nowrap",
      transition: "transform 0.18s ease, box-shadow 0.2s ease, background-color 0.2s ease",
      "&:hover": { transform: "translateY(-1px)" },
      "&:active": { transform: "translateY(0)" },
      "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 3 },
      "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
      ...sx,
    },
  };

  if (action.kind === "route") {
    return (
      <Button {...base} component={RouterLink} to={action.to} onClick={onNavigate}>
        {action.label}
      </Button>
    );
  }

  if (action.kind === "workspace") {
    const href = ambassadorUrl(action.path);
    const onClick = (e) => {
      if (isModifiedClick(e)) return;
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      onNavigate?.();
      // openWorkspace navigates away on success and falls back to a plain
      // navigation itself. Anything thrown before that still gets the reader
      // to the destination, never a dead button.
      Promise.resolve()
        .then(() => openWorkspace("ambassador", action.path))
        .catch((err) => {
          console.error("[ambassador/landing] workspace handoff failed", err);
          setBusy(false);
          window.location.assign(href);
        });
    };
    return (
      <Button {...base} component="a" href={href} onClick={onClick} aria-busy={busy || undefined}>
        {action.label}
      </Button>
    );
  }

  return (
    <Button {...base} component="a" href={action.href} onClick={onNavigate}>
      {action.label}
    </Button>
  );
}

/** "Enrolment opens soon": stated plainly, never a disabled button. */
export function EnrolmentClosedNote({ sx }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      role="status"
      sx={{
        px: 2, py: 1.25, borderRadius: 2,
        border: "1px solid",
        borderColor: (t) => alpha(t.palette.warning.main, 0.4),
        bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.12 : 0.08),
        color: "text.primary",
        ...sx,
      }}
    >
      <ScheduleRoundedIcon sx={{ fontSize: 20, color: "warning.main" }} aria-hidden="true" />
      <Box>
        <Typography component="p" sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
          Enrolment opens soon
        </Typography>
        <Typography component="p" sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.4 }}>
          New Ambassador sign-ups are paused for now.
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * The page's primary + secondary action pair.
 * `cta` comes from resolveCta() in pages/PartnersLanding.js.
 */
export function CtaGroup({ cta, align = "left", sx }) {
  if (!cta) return null;
  const justify = align === "center" ? "center" : "flex-start";
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent={justify}
      useFlexGap
      flexWrap="wrap"
      sx={sx}
    >
      {cta.closed && !cta.primary && <EnrolmentClosedNote />}
      {cta.primary && <CtaButton action={cta.primary} variant="contained" />}
      {cta.secondary && <CtaButton action={cta.secondary} variant="outlined" />}
    </Stack>
  );
}
